import React, {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import { Doughnut } from 'react-chartjs-2';
import logoIcon from "../../assets/logo-icon.png";

const translations = {
  en: {
    bigFiveAnalysis: "Big Five Analysis (OCEAN Model)",
    yourPersonalityProfile: "Your Personality Profile (OCEAN Model)",
    showWorkplaceImpact: "Show Workplace Impact",
    showDevelopmentTip: "Show Development Tip",
    workplaceImpact: "Workplace Impact",
    developmentTip: "Development Tip",
    workBehaviorAssessment: "Work Behavior Assessment",
    yourWorkBehaviorStyle: "Your Work Behavior Style",
    teamFitInsight: "Team Fit Insight",
    careerGrowthTip: "Career Growth Tip"
  },
  nl: {
    bigFiveAnalysis: "Big Five Analyse (OCEAN Model)",
    yourPersonalityProfile: "Jouw Persoonlijkheidsprofiel (OCEAN Model)",
    showWorkplaceImpact: "Toon Werkplek Impact",
    showDevelopmentTip: "Toon Ontwikkelingstip",
    workplaceImpact: "Werkplek Impact",
    developmentTip: "Ontwikkelingstip",
    workBehaviorAssessment: "Werkgedrag Beoordeling",
    yourWorkBehaviorStyle: "Jouw Werkgedrag Stijl",
    teamFitInsight: "Team Fit Inzicht",
    careerGrowthTip: "Carrière Groei Tip"
  }
};

const BigFiveChart = ({ bigFiveData, analysisResults, isRecruiterMode, isGeneratingPDF, isDutch = false }) => {
    const [lang, setLang] = useState(isDutch ? 'nl' : 'en');
    const t = translations[lang];
    const [isBigFiveToggleOpen, setIsbigFiveToggleOpen] = useState(false);

    const chartOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                }
            }
        }
    };

    const bigFiveColors = [
        '#FFD700', 
        '#FFA500', 
        '#FF8C00',
        '#EA650D', 
        '#E55100',
        '#D84315', 
    ]

    const toggleOpen = () => {
        setIsbigFiveToggleOpen(!isBigFiveToggleOpen);
        Cookies.set('isBigFiveToggleOpen', !isBigFiveToggleOpen)
    };

    useEffect(() => {
        const checkLanguage = () => {
            const cookieIsDutch = Cookies.get('isDutch') === 'true';
            const newLang = cookieIsDutch ? 'nl' : 'en';
            if (newLang !== lang) {
                setLang(newLang);
            }
        };

        // Check immediately
        checkLanguage();

        // Set up an interval to check for changes
        const interval = setInterval(checkLanguage, 1000);

        return () => clearInterval(interval);
    }, [lang]);

    useEffect(() => {
        setIsbigFiveToggleOpen(true)
    }, [isRecruiterMode])

    return (
        <div className="flex flex-col animate-text">
            <div className='flex items-center justify-between gap-2'>
            <h2 className="text-xl font-bold text-primary">
                {isRecruiterMode ? t.bigFiveAnalysis : t.yourPersonalityProfile}
            </h2>
            {!isGeneratingPDF && (
            <div className="toggle-container !m-0 !gap-2">
            <label className='text-sm'>
            {isRecruiterMode ? t.showWorkplaceImpact : t.showDevelopmentTip}
            </label>
            <input
                type="checkbox"
                checked={isBigFiveToggleOpen}
                onChange={toggleOpen}
                className="theme-checkbox"
                />
            </div>
            )}
            </div>
            <div className="flex-1 border rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                <div className="w-full h-[300px] flex items-center justify-center mt-4 animate-text">
                    <Doughnut data={bigFiveData} options={chartOptions} />
                </div>
                <div className="grid grid-cols-2 gap-8 p-4 ">
                    {Object.entries(analysisResults?.bigFive)?.map(([trait, data], index) => (
                        trait !== 'workBehavior' && trait !== 'teamFitInsight' && trait !== 'careerGrowthTip' && (
                            <div className="animate-text flex flex-col" key={index}>
                                <div className='flex-1'>
                                    <h3 className="text-primary font-bold capitalize">{trait}</h3>
                                    <div className="flex justify-between gap-2 items-center mt-2">
                                        <h3 className="font-semibold capitalize text-sm">{data.level}</h3>
                                        <p className="text-gray-500 text-sm">{data.score}%</p>
                                    </div>
                                    <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                        <div
                                            className={`absolute h-full bg-default rounded`}
                                            style={{ 
                                                width: `${(data.score)}%`, 
                                                backgroundColor: bigFiveColors[index]
                                            }}
                                        />
                                    </div>

                                    <div className="text-sm mt-4">
                                            <p className="text-gray-600">{data.interpretation}</p>
                                    </div>
                                </div>
                                    
                                {isRecruiterMode ? (
                                    <div className=''>
                                        {isBigFiveToggleOpen && data.workplaceImpact && (
                                            <div className="text-xs mt-4 font-medium p-2 rounded-md gradient-bg-1">
                                                <p><span className="font-semibold">{t.workplaceImpact}:</span> {data.workplaceImpact}</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className=''>
                                        {isBigFiveToggleOpen && data.developmentTip && (
                                            <div className="text-xs mt-4 font-medium p-2 rounded-md gradient-bg-1 ">
                                                <p><span className="font-semibold">{t.developmentTip}:</span> {data.developmentTip}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    ))}
                </div>
                <div className="mt-2 border-top p-4 animate-text">
                <div className="grid grid-cols-2 gap-4 mb-1">
                <div className='p-4 rounded-md gradient-bg-2 flex-1'>
                <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                        <img src={logoIcon} className="size-4" />
                            <h3>{isRecruiterMode ? t.workBehaviorAssessment : t.yourWorkBehaviorStyle}</h3>
                        </span>
                        <p className="animate-text mt-2">{analysisResults.bigFive.workBehavior}</p>
                    </div>

                    {isRecruiterMode ? (
                        analysisResults.bigFive.teamFitInsight && (
                            <div className='p-4 rounded-md gradient-bg-2 flex-1'>
                                <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                                <img src={logoIcon} className="size-4" />
                                    <h3>{t.teamFitInsight}</h3>
                                </span>
                                <p className="animate-text mt-2">{analysisResults.bigFive.teamFitInsight}</p>
                            </div>
                        )
                    ) : (
                        analysisResults.bigFive.careerGrowthTip && (
                            <div className='p-4 rounded-md gradient-bg-2 flex-1'>
                                <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                                <img src={logoIcon} className="size-4" />
                                <h3>{t.careerGrowthTip}</h3>
                                </span>
                                <p className="animate-text mt-2">{analysisResults.bigFive.careerGrowthTip}</p>
                             </div>
                        )
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BigFiveChart; 